import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '@frontegg/react'
import moment from 'moment'
import { Application, ApplicationManifest, IntegrationsSort } from 'models'
import { hasAnyRole, Role } from 'lib/utils'
import { useAppDispatch } from 'store/hooks'
import { ModalType, openModal } from 'store/modals/modalSlice'
import { closeToast, openToast, ToastVariant } from 'store/toasts/toastSlice'
import { ApplicationsServiceV4 } from 'services/v4/applications-v4.service'
import { integrationsInstancesOptions } from '../../integrations.constants'
import { freshStartPath } from '../applicationSetup.constants'
import { discardAbandonedApplication } from '../abandonedSetup'
import {
  CatalogTab,
  INSTANCE_DELETED_TITLE,
  LOAD_FAILED_MESSAGE,
  REMOVE_FAILED_MESSAGE,
  instanceDeletedDescription
} from './applicationsCatalog.constants'

export interface DirectoryFilters {
  category: string[]
  vendor: string[]
  configurationStatus: string[]
}

export interface InstanceFilters {
  category: string[]
  vendor: string[]
  product: string[]
}

/* A live application with the manifest it points at, which supplies its logo, vendor and categories. */
export interface CatalogInstance {
  application: Application
  manifest: ApplicationManifest
}

interface ApplicationsCatalog {
  tab: CatalogTab
  changeTab: (tab: CatalogTab) => void
  loading: boolean
  /* Bumped on every reset so the search box, which keeps its own text, remounts empty. */
  resetKey: number
  catalogEmpty: boolean
  newManifests: ApplicationManifest[]
  allManifests: ApplicationManifest[]
  instanceCounts: Record<string, number>
  directorySearch: string
  setDirectorySearch: (search: string) => void
  directoryFilters: DirectoryFilters
  setDirectoryFilter: (field: keyof DirectoryFilters, values: string[]) => void
  directorySort: IntegrationsSort
  setDirectorySort: (sort: IntegrationsSort) => void
  directoryOptions: Pick<DirectoryFilters, 'category' | 'vendor'>
  resetDirectory: () => void
  noInstances: boolean
  instances: CatalogInstance[]
  instanceSearch: string
  setInstanceSearch: (search: string) => void
  instanceFilters: InstanceFilters
  setInstanceFilter: (field: keyof InstanceFilters, values: string[]) => void
  instanceSort: IntegrationsSort
  setInstanceSort: (sort: IntegrationsSort) => void
  instanceOptions: InstanceFilters
  resetInstances: () => void
  directoryCount: number
  instancesCount: number
  addInstance: (manifest: ApplicationManifest) => void
  viewInstances: (manifest: ApplicationManifest) => void
  paneOpen: boolean
  paneManifest: ApplicationManifest | null
  /* Set when the pane shows one instance rather than its manifest. */
  paneApplication: Application | null
  paneInstances: Application[]
  openManifestPane: (manifest: ApplicationManifest) => void
  openInstancePane: (instance: CatalogInstance) => void
  closePane: () => void
  noEditPermission: boolean
  confirmRemove: (instance: CatalogInstance) => void
}

const [CONFIGURED, NOT_CONFIGURED] = integrationsInstancesOptions

const EMPTY_DIRECTORY_FILTERS: DirectoryFilters = { category: [], vendor: [], configurationStatus: [] }

const EMPTY_INSTANCE_FILTERS: InstanceFilters = { category: [], vendor: [], product: [] }

export const countInstances = (applications: Application[]): Record<string, number> =>
  applications.reduce<Record<string, number>>((counts, application) => {
    counts[application.manifest_id] = (counts[application.manifest_id] || 0) + 1
    return counts
  }, {})

/* The inbound page's rule, kept identical so both pages call the same integrations new. */
export const isManifestNew = (manifest: ApplicationManifest): boolean =>
  moment().diff(moment(manifest.published_at), 'days') + 1 < 30

const uniqueSorted = (values: string[]): string[] => Array.from(new Set(values)).sort((a, b) => a.localeCompare(b))

const byText = (a: string, b: string) => a.toLowerCase().localeCompare(b.toLowerCase())

const timeOf = (date?: string | null) => (date ? moment(date).valueOf() : 0)

const matchesSearch = (text: string, search: string) => text.toLowerCase().includes(search.trim().toLowerCase())

const matchesAny = (selected: string[], values: string[]) =>
  !selected.length || values.some((value) => selected.includes(value))

export const filterManifests = (
  manifests: ApplicationManifest[],
  counts: Record<string, number>,
  search: string,
  filters: DirectoryFilters
): ApplicationManifest[] => {
  const wantsConfigured = filters.configurationStatus.includes(CONFIGURED)
  const wantsNotConfigured = filters.configurationStatus.includes(NOT_CONFIGURED)

  return manifests.filter((manifest) => {
    const configured = (counts[manifest.id] || 0) > 0

    return (
      matchesSearch(manifest.display_name, search) &&
      matchesAny(filters.category, manifest.supported_categories) &&
      matchesAny(filters.vendor, [manifest.vendor.display_name]) &&
      (wantsConfigured === wantsNotConfigured || configured === wantsConfigured)
    )
  })
}

export const sortManifests = (manifests: ApplicationManifest[], sort: IntegrationsSort): ApplicationManifest[] => {
  const byName = (a: ApplicationManifest, b: ApplicationManifest) => byText(a.display_name, b.display_name)
  const byPublished = (a: ApplicationManifest, b: ApplicationManifest) =>
    timeOf(a.published_at) - timeOf(b.published_at)

  const sorted = [...manifests]
  switch (sort) {
    case IntegrationsSort.ZA:
      return sorted.sort((a, b) => byName(b, a))
    case IntegrationsSort.NewestFirst:
      return sorted.sort((a, b) => byPublished(b, a))
    case IntegrationsSort.OldestFirst:
      return sorted.sort(byPublished)
    default:
      return sorted.sort(byName)
  }
}

/* The catalog entry first: the manifest nested in a list answer carries no categories. It stands in
 * for a manifest the catalog does not list, so an instance of one still has a card. */
export const pairWithManifests = (applications: Application[], manifests: ApplicationManifest[]): CatalogInstance[] =>
  applications.flatMap((application) => {
    const manifest = manifests.find((m) => m.id === application.manifest_id) || application.manifest
    return manifest ? [{ application, manifest }] : []
  })

export const filterInstances = (
  instances: CatalogInstance[],
  search: string,
  filters: InstanceFilters
): CatalogInstance[] =>
  instances.filter(
    ({ application, manifest }) =>
      matchesSearch(application.display_name, search) &&
      matchesAny(filters.category, manifest.supported_categories) &&
      matchesAny(filters.vendor, [manifest.vendor.display_name]) &&
      matchesAny(filters.product, [manifest.display_name])
  )

export const sortInstances = (instances: CatalogInstance[], sort: IntegrationsSort): CatalogInstance[] => {
  const byName = (a: CatalogInstance, b: CatalogInstance) =>
    byText(a.application.display_name, b.application.display_name)
  const byCreated = (a: CatalogInstance, b: CatalogInstance) =>
    timeOf(a.application.created_at) - timeOf(b.application.created_at)

  const sorted = [...instances]
  switch (sort) {
    case IntegrationsSort.ZA:
      return sorted.sort((a, b) => byName(b, a))
    case IntegrationsSort.LatestCreated:
      return sorted.sort((a, b) => byCreated(b, a))
    case IntegrationsSort.OldestCreated:
      return sorted.sort(byCreated)
    default:
      return sorted.sort(byName)
  }
}

/* Owns what the Ticketing Systems page fetches and derives, so the page component only renders. */
export const useApplicationsCatalog = (): ApplicationsCatalog => {
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const { user: authUser } = useAuth()
  const closeNotification = useRef<any>(null)

  const [tab, setTab] = useState<CatalogTab>(CatalogTab.Directory)
  const [manifests, setManifests] = useState<ApplicationManifest[]>([])
  const [applications, setApplications] = useState<Application[]>([])
  const [loading, setLoading] = useState(true)
  const [resetKey, setResetKey] = useState(0)
  const [directorySearch, setDirectorySearch] = useState('')
  const [directoryFilters, setDirectoryFilters] = useState<DirectoryFilters>(EMPTY_DIRECTORY_FILTERS)
  const [directorySort, setDirectorySort] = useState<IntegrationsSort>(IntegrationsSort.AZ)
  const [instanceSearch, setInstanceSearch] = useState('')
  const [instanceFilters, setInstanceFilters] = useState<InstanceFilters>(EMPTY_INSTANCE_FILTERS)
  const [instanceSort, setInstanceSort] = useState<IntegrationsSort>(IntegrationsSort.AZ)
  /* Kept apart from the selection so the pane can slide out with its content still in place. */
  const [paneOpen, setPaneOpen] = useState(false)
  const [paneManifest, setPaneManifest] = useState<ApplicationManifest | null>(null)
  const [paneApplication, setPaneApplication] = useState<Application | null>(null)

  const notify = useCallback(
    (variant: ToastVariant, text: string, description = '') => {
      clearTimeout(closeNotification.current)
      dispatch(openToast({ variant, props: { text, description } }))
      closeNotification.current = setTimeout(() => dispatch(closeToast()), 5000)
    },
    [dispatch]
  )

  useEffect(() => {
    const load = async () => {
      try {
        await discardAbandonedApplication()
        const [loadedManifests, loadedApplications] = await Promise.all([
          ApplicationsServiceV4.listManifests(),
          ApplicationsServiceV4.listLiveApplications()
        ])
        setManifests(loadedManifests)
        setApplications(loadedApplications)
      } catch {
        notify(ToastVariant.error, LOAD_FAILED_MESSAGE)
      } finally {
        setLoading(false)
      }
    }

    load()
  }, [notify])

  useEffect(() => () => clearTimeout(closeNotification.current), [])

  const instanceCounts = useMemo(() => countInstances(applications), [applications])

  const directoryOptions = useMemo(
    () => ({
      category: uniqueSorted(manifests.flatMap((m) => m.supported_categories)),
      vendor: uniqueSorted(manifests.map((m) => m.vendor.display_name))
    }),
    [manifests]
  )

  const [newManifests, allManifests] = useMemo(() => {
    const shown = sortManifests(
      filterManifests(manifests, instanceCounts, directorySearch, directoryFilters),
      directorySort
    )
    return [shown.filter(isManifestNew), shown.filter((manifest) => !isManifestNew(manifest))]
  }, [manifests, instanceCounts, directorySearch, directoryFilters, directorySort])

  const allInstances = useMemo(() => pairWithManifests(applications, manifests), [applications, manifests])

  const instanceOptions = useMemo(
    () => ({
      category: uniqueSorted(allInstances.flatMap(({ manifest }) => manifest.supported_categories)),
      vendor: uniqueSorted(allInstances.map(({ manifest }) => manifest.vendor.display_name)),
      product: uniqueSorted(allInstances.map(({ manifest }) => manifest.display_name))
    }),
    [allInstances]
  )

  const instances = useMemo(
    () => sortInstances(filterInstances(allInstances, instanceSearch, instanceFilters), instanceSort),
    [allInstances, instanceSearch, instanceFilters, instanceSort]
  )

  const paneInstances = useMemo(
    () => (paneManifest ? applications.filter((application) => application.manifest_id === paneManifest.id) : []),
    [applications, paneManifest]
  )

  const setDirectoryFilter = (field: keyof DirectoryFilters, values: string[]) =>
    setDirectoryFilters((current) => ({ ...current, [field]: values }))

  const setInstanceFilter = (field: keyof InstanceFilters, values: string[]) =>
    setInstanceFilters((current) => ({ ...current, [field]: values }))

  const resetDirectory = () => {
    setDirectorySearch('')
    setDirectoryFilters(EMPTY_DIRECTORY_FILTERS)
    setDirectorySort(IntegrationsSort.AZ)
    setResetKey((key) => key + 1)
  }

  const resetInstances = (filters: InstanceFilters = EMPTY_INSTANCE_FILTERS) => {
    setInstanceSearch('')
    setInstanceFilters(filters)
    setInstanceSort(IntegrationsSort.AZ)
    setResetKey((key) => key + 1)
  }

  const noEditPermission = useMemo(() => hasAnyRole([Role.Viewer, Role.Demo], authUser), [authUser])

  const openManifestPane = (manifest: ApplicationManifest) => {
    setPaneManifest(manifest)
    setPaneApplication(null)
    setPaneOpen(true)
  }

  const openInstancePane = ({ application, manifest }: CatalogInstance) => {
    setPaneManifest(manifest)
    setPaneApplication(application)
    setPaneOpen(true)
  }

  const closePane = () => setPaneOpen(false)

  /* Answers once the outcome is known, so the confirmation can close after its toast. */
  const removeInstance = async ({ application, manifest }: CatalogInstance) => {
    try {
      await ApplicationsServiceV4.removeApplication(application.id)
    } catch {
      notify(ToastVariant.error, REMOVE_FAILED_MESSAGE)
      return
    }

    notify(
      ToastVariant.success,
      INSTANCE_DELETED_TITLE,
      instanceDeletedDescription(application.display_name, manifest.display_name)
    )
    closePane()

    try {
      setApplications(await ApplicationsServiceV4.listLiveApplications())
    } catch {
      notify(ToastVariant.error, LOAD_FAILED_MESSAGE)
    }
  }

  const confirmRemove = (instance: CatalogInstance) =>
    dispatch(
      openModal({
        type: ModalType.deleteApplicationInstance,
        props: {
          deleteApplicationInstanceProps: {
            displayName: instance.application.display_name,
            onConfirm: () => removeInstance(instance)
          }
        }
      })
    )

  /* Switching tabs by hand starts from an unfiltered list, as on the inbound page. */
  const changeTab = (next: CatalogTab) => {
    closePane()
    resetDirectory()
    resetInstances()
    setTab(next)
  }

  const addInstance = (manifest: ApplicationManifest) => navigate(freshStartPath(manifest.slug))

  const viewInstances = (manifest: ApplicationManifest) => {
    closePane()
    resetInstances({ ...EMPTY_INSTANCE_FILTERS, product: [manifest.display_name] })
    setTab(CatalogTab.Instances)
  }

  return {
    tab,
    changeTab,
    loading,
    resetKey,
    catalogEmpty: !manifests.length,
    newManifests,
    allManifests,
    instanceCounts,
    directorySearch,
    setDirectorySearch,
    directoryFilters,
    setDirectoryFilter,
    directorySort,
    setDirectorySort,
    directoryOptions,
    resetDirectory,
    noInstances: !allInstances.length,
    instances,
    instanceSearch,
    setInstanceSearch,
    instanceFilters,
    setInstanceFilter,
    instanceSort,
    setInstanceSort,
    instanceOptions,
    resetInstances: () => resetInstances(),
    directoryCount: newManifests.length + allManifests.length,
    instancesCount: instances.length,
    addInstance,
    viewInstances,
    paneOpen,
    paneManifest,
    paneApplication,
    paneInstances,
    openManifestPane,
    openInstancePane,
    closePane,
    noEditPermission,
    confirmRemove
  }
}
